import { MouseEvent, KeyboardEvent } from "react";
import { PropertyResult } from "../../../../services/api";
import { useSearchStore } from "../../../../store/searchStore";
import { mapConfig, loginRedirectUrl } from "../../../../config/mapConfig";
import styles from "./index.module.scss";

/**
 * The card lives inside the uSkinned/Umbraco page, whose theme CSS forces a
 * height on content elements with `!important` and higher specificity than a
 * CSS-module class can beat. Inline styles set with priority ("important")
 * outrank ANY external stylesheet rule, so this is the only reliable way to
 * let the card grow to fit a multi-line address.
 */
function pinHeight(el: HTMLDivElement | null) {
  if (!el) return;
  el.style.setProperty("height", "auto", "important");
  el.style.setProperty("max-height", "none", "important");
  el.style.setProperty("min-height", "19rem", "important");
  el.style.setProperty("overflow", "visible", "important");
}

/** 67 active listings carry no price; say so instead of rendering "$0". */
function formatPrice(n: number): string {
  if (!n || n <= 0) return "Price on request";
  return `$${n.toLocaleString("en-US")}`;
}

function formatNumber(n: number): string {
  return n.toLocaleString("en-US");
}

function buildStats(p: PropertyResult): string[] {
  const parts: string[] = [];
  if (p.beds > 0) parts.push(`${p.beds} bd`);
  if (p.baths > 0) parts.push(`${p.baths} ba`);
  if (p.sqft > 0) parts.push(`${formatNumber(p.sqft)} sqft`);
  if (p.acres > 0 && p.sqft === 0) parts.push(`${p.acres} acres`);
  return parts;
}

/** Spark photo URLs carry width/height params: derive a small srcset. */
function buildSrcSet(url: string): string | undefined {
  try {
    const u = new URL(url, window.location.origin);
    if (!u.searchParams.has("width")) return undefined;
    return [480, 800, 1200]
      .map((w) => {
        const v = new URL(u.toString());
        v.searchParams.set("width", String(w));
        v.searchParams.set("height", String(Math.round((w * 3) / 4)));
        return `${v.toString()} ${w}w`;
      })
      .join(", ");
  } catch {
    return undefined;
  }
}

interface PropertyCardProps {
  property: PropertyResult;
}

/**
 * Zillow-style listing card. Div with role=link + onClick so ambient
 * anchor-targeting CSS (from Umbraco/uSkinned) can't collapse it.
 * Heart button overlay toggles "saved" state (persisted server-side to the
 * member's favorites via the store; signed-out clicks redirect to login).
 */
export default function PropertyCard({ property }: PropertyCardProps) {
  const hoveredId = useSearchStore((s) => s.hoveredPropertyId);
  const activeId = useSearchStore((s) => s.activePropertyId);
  const setHovered = useSearchStore((s) => s.setHoveredProperty);
  const savedIds = useSearchStore((s) => s.savedPropertyIds);
  const toggleSaved = useSearchStore((s) => s.toggleSavedProperty);
  const openListModal = useSearchStore((s) => s.openListModal);

  const hasImage = !!(property.image && property.image.length > 0);
  // A clicked map pin highlights its card the same way a hover does.
  const isHovered = hoveredId === property.id || activeId === property.id;
  const isSaved = savedIds.includes(property.id);
  const stats = buildStats(property);

  // `address` is the MLS UnparsedAddress, which usually already contains the
  // city/state/zip. Only show the separate city/state line when it doesn't
  // (i.e. the street-only fallback), so we never duplicate or clip it.
  const locationLine = [property.city, property.state].filter(Boolean).join(", ");
  const addressHasCity = !!(
    property.city && property.address.toLowerCase().includes(property.city.toLowerCase())
  );

  const go = () => {
    if (property.url) window.location.href = property.url;
  };

  const handleSave = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Signed-out visitors must log in first. Send them to the member area;
    // Umbraco public access bounces them to the login page. Mirrors the
    // Save Search button so the two flows behave identically.
    if (!mapConfig.isSignedIn) {
      const login = loginRedirectUrl();
      if (login) window.location.href = login;
      return;
    }

    void toggleSaved(property.id);
  };

  const handleAddToList = (e: MouseEvent | KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Signed-out visitors must log in first (mirrors the heart).
    if (!mapConfig.isSignedIn) {
      const login = loginRedirectUrl();
      if (login) window.location.href = login;
      return;
    }

    openListModal(property.id);
  };

  return (
    <div
      ref={pinHeight}
      role="link"
      tabIndex={0}
      aria-label={`${property.address}, ${property.city}, ${formatPrice(property.price)}`}
      className={`${styles.card} ${isHovered ? styles.cardHovered : ""}`}
      data-listing-id={property.id}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          go();
        }
      }}
      onMouseEnter={() => setHovered(property.id)}
      onMouseLeave={() => setHovered(null)}
      onFocus={() => setHovered(property.id)}
      onBlur={() => setHovered(null)}
    >
      <div className={styles.image}>
        {hasImage ? (
          <img
            className={styles.img}
            src={property.image}
            srcSet={buildSrcSet(property.image)}
            sizes="(min-width: 1280px) 17rem, 100vw"
            loading="lazy"
            decoding="async"
            alt={property.address}
          />
        ) : (
          <div className={styles.noImage} aria-hidden="true" />
        )}

        <button
          type="button"
          className={styles.listBtn}
          aria-label="Add to a list"
          title="Add to a list"
          onClick={handleAddToList}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleAddToList(e);
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 5v14M5 12h14"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <button
          type="button"
          className={`${styles.saveBtn} ${isSaved ? styles.saveBtnActive : ""}`}
          aria-label={isSaved ? "Remove from favorites" : "Save to favorites"}
          title={isSaved ? "Remove from favorites" : "Save to favorites"}
          aria-pressed={isSaved}
          onClick={handleSave}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleSave(e);
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M12 21s-7.5-4.5-9.5-9.5C1 8 3 4.5 6.5 4.5c2 0 3.5 1 5.5 3 2-2 3.5-3 5.5-3 3.5 0 5.5 3.5 4 7C19.5 16.5 12 21 12 21z"
              fill={isSaved ? "currentColor" : "none"}
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <div className={styles.body}>
        <div className={styles.price}>{formatPrice(property.price)}</div>
        {stats.length > 0 && <div className={styles.stats}>{stats.join(" · ")}</div>}
        <div className={styles.address}>{property.address}</div>
        {!addressHasCity && locationLine && <div className={styles.meta}>{locationLine}</div>}
        {property.listOffice && (
          <div className={styles.courtesy}>Listing courtesy of {property.listOffice}</div>
        )}
      </div>
    </div>
  );
}
